import { useEffect } from "react";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/components/language-provider";
import { WEBSITE_ROUTES, APP_URL } from "@/lib/config";
import { PageHeader } from "@/components/page-header";
import { PcfShowcase, TemplateMappingMock, BrandPair, VideoFacade } from "@/components/landing/product-mocks";
import { Reveal } from "@/components/landing/reveal";
import { Check, Download, Settings2, LayoutTemplate, Send, type LucideIcon } from "lucide-react";

const VIDEOS = { pt: "kj-qT9FmzWU", en: "qIGd0KSpj5U" };

export default function Dynamics365() {
  const { language } = useLanguage();
  const pt = language === "pt";
  const es = language === "es";
  useEffect(() => { window.scrollTo({ top: 0, left: 0, behavior: "auto" }); }, []);
  const forest = "text-[var(--fs-forest)]";

  const install: Array<[LucideIcon, string, string]> = pt
    ? [[Download, "Importe a solução", "Solução gerenciada do FillSign na sua organização (Power Platform → Soluções → Importar). Sem código."],
       [Settings2, "Configure o controle", "Coloque o componente PCF numa seção do formulário (Conta, Oportunidade ou qualquer entidade) e escolha: upload, template ou os dois."],
       [LayoutTemplate, "Mapeie os templates", "No FillSign, monte o contrato com campos do CRM. Cada envio já sai preenchido com o registro aberto."],
       [Send, "Envie do registro", "Assinantes vêm dos contatos e usuários; a IA posiciona; status, reenvio e auditoria ficam no próprio registro."]]
    : es ? [[
    Download,
    "Importe la solución",
    "Solución administrada de FillSign en su organización (Power Platform → Soluciones → Importar). Sin código."
  ], [
    Settings2,
    "Configure el control",
    "Coloque el componente PCF en una sección del formulario (Cuenta, Oportunidad o cualquier entidad) y elija: carga, plantilla o ambos."
  ], [
    LayoutTemplate,
    "Mapee las plantillas",
    "En FillSign, arme el contrato con campos del CRM. Cada envío sale completado con el registro abierto."
  ], [
    Send,
    "Envíe desde el registro",
    "Los firmantes vienen de los contactos y usuarios; la IA posiciona las firmas; el estado, el reenvío y la auditoría quedan en el registro."
  ]] : [[Download, "Import the solution", "FillSign managed solution in your org (Power Platform → Solutions → Import). No code."],
       [Settings2, "Configure the control", "Drop the PCF into a form section (Account, Opportunity or any entity) and choose: upload, template or both."],
       [LayoutTemplate, "Map the templates", "In FillSign, build the contract with CRM fields. Every send goes out filled from the open record."],
       [Send, "Send from the record", "Signers come from contacts and users; AI places signatures; status, resend and audit trail stay on the record."]];

  const bullets = pt
    ? ["Instala em minutos, sem desenvolvedor", "Qualquer entidade, inclusive customizada", "Upload de PDF/Word ou template com campos do CRM", "Assinantes dos contatos e usuários do registro", "IA sugere as posições; você confirma", "Reenvio por e-mail e WhatsApp, auditoria, cancelamento", "Interface em português, inglês e espanhol", "Sem sair do formulário"]
    : es ? [
    "Se instala en minutos, sin desarrollador",
    "Cualquier entidad, incluso personalizada",
    "Carga de PDF/Word o plantilla con campos del CRM",
    "Firmantes de los contactos y usuarios del registro",
    "La IA sugiere las posiciones; usted confirma",
    "Reenvío por correo y WhatsApp, auditoría, cancelación",
    "Interfaz en portugués, inglés y español",
    "Sin salir del formulario"
  ] : ["Installs in minutes, no developer", "Any entity, including custom", "PDF/Word upload or template with CRM fields", "Signers from the record's contacts and users", "AI suggests positions; you confirm", "Resend by email and WhatsApp, audit trail, cancel", "Interface in Portuguese, English and Spanish", "Never leave the form"];

  return (
    <div className="min-h-screen bg-white text-[#1B2A1E]">
      <PageHeader />

      <section className="mx-auto max-w-6xl px-5 pt-14 pb-10 sm:px-8 lg:pt-20">
        <div className="grid items-end gap-10 lg:grid-cols-[1.1fr_1fr]">
          <div>
            <div className="mb-6 inline-flex items-center gap-3 rounded-full bg-[var(--fs-d365-blue)] p-2 pr-5 text-sm text-white"><BrandPair />{pt ? "Componente nativo (PCF)" : es ? "Componente nativo (PCF)" : "Native component (PCF)"}</div>
            <h1 className={`text-4xl font-semibold leading-[1.05] tracking-[-0.03em] sm:text-5xl ${forest}`}>
              {pt ? "Assinaturas sem sair do Dynamics 365" : es ? "Firmas sin salir de Dynamics 365" : "Signatures without leaving Dynamics 365"}
            </h1>
            <p className="mt-6 max-w-xl text-lg leading-relaxed text-gray-600">
              {pt
                ? "Um componente instalado na sua organização. O vendedor escolhe o documento ou o template, a IA posiciona as assinaturas e o contrato sai da própria Conta ou Oportunidade. Status, PDF assinado e auditoria voltam pro registro."
                : es ? "Un componente instalado en su organización. El vendedor elige el documento o la plantilla, la IA posiciona las firmas y el contrato sale desde la propia Cuenta u Oportunidad. El estado, el PDF firmado y la auditoría vuelven al registro." : "A component installed in your org. The rep picks the document or template, AI places the signatures and the contract goes out from the Account or Opportunity itself. Status, signed PDF and audit trail flow back to the record."}
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Link href={WEBSITE_ROUTES.enterpriseContact}><Button size="lg" className="h-12 w-full rounded-md bg-[var(--fs-moss)] px-6 text-base text-white hover:bg-[var(--fs-forest)] sm:w-auto">{pt ? "Falar com vendas" : es ? "Hablar con ventas" : "Talk to sales"}</Button></Link>
              <a href={`${APP_URL}/register`}><Button size="lg" variant="outline" className="h-12 w-full rounded-md border-gray-300 px-6 text-base sm:w-auto">{pt ? "Criar conta" : es ? "Crear cuenta" : "Create account"}</Button></a>
            </div>
          </div>
          <ul className="grid gap-3 text-sm sm:grid-cols-2">
            {bullets.map((i) => <li key={i} className="flex items-start gap-2 rounded-md border border-black/10 px-3 py-2"><Check className="mt-0.5 h-4 w-4 shrink-0 text-[var(--fs-moss)]" />{i}</li>)}
          </ul>
        </div>
      </section>

      <section className="bg-[var(--fs-d365-blue)] text-white">
        <div className="mx-auto max-w-6xl px-5 py-16 sm:px-8 lg:py-20">
          <Reveal>
            <h2 className="text-3xl font-semibold tracking-[-0.02em] sm:text-4xl">{pt ? "Como fica dentro do formulário" : es ? "Cómo se ve dentro del formulario" : "What it looks like inside the form"}</h2>
            <p className="mt-3 max-w-2xl text-white/75">{pt ? "Formulário padrão de Conta, seção FillSign. Três momentos reais do componente." : es ? "Formulario estándar de Cuenta, sección FillSign. Tres momentos reales del componente." : "Standard Account form, FillSign section. Three real moments of the component."}</p>
          </Reveal>
          <Reveal className="mt-10 min-w-0" variant="scale"><PcfShowcase pt={pt} /></Reveal>
          <div className="mt-16 grid items-center gap-8 lg:grid-cols-[1fr_1.4fr]">
            <Reveal>
              <h3 className="text-2xl font-semibold tracking-[-0.02em]">{pt ? "Demo de 3 minutos" : es ? "Demo de 3 minutos" : "3-minute demo"}</h3>
              <p className="mt-3 text-white/75">{pt ? "Do documento ao envio, num registro de Conta." : es ? "Del documento al envío, en un registro de Cuenta." : "From document to send, on an Account record."}</p>
            </Reveal>
            <Reveal variant="scale"><VideoFacade id={pt ? VIDEOS.pt : VIDEOS.en} title="FillSign · Dynamics 365" /></Reveal>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-5 py-20 sm:px-8 lg:py-24">
        <Reveal>
          <h2 className={`text-3xl font-semibold tracking-[-0.02em] sm:text-4xl ${forest}`}>{pt ? "Templates preenchidos com os dados do CRM" : es ? "Plantillas completadas con datos del CRM" : "Templates filled with CRM data"}</h2>
          <p className="mt-3 max-w-2xl text-lg text-gray-600">{pt ? "Monte uma vez, envie sempre preenchido: nome da conta, CNPJ, valor da oportunidade, contato." : es ? "Arme una vez y envíe siempre completado: nombre de la cuenta, ID fiscal, monto de la oportunidad, contacto." : "Build once, send filled every time: account name, tax ID, opportunity amount, contact."}</p>
        </Reveal>
        <Reveal className="mt-10 min-w-0" variant="scale"><TemplateMappingMock pt={pt} /></Reveal>
      </section>

      <section className="border-y border-black/5 bg-[#F7FAF3]">
        <div className="mx-auto max-w-6xl px-5 py-20 sm:px-8 lg:py-24">
          <Reveal><h2 className={`text-3xl font-semibold tracking-[-0.02em] sm:text-4xl ${forest}`}>{pt ? "Instalação em quatro passos" : es ? "Instalación en cuatro pasos" : "Installation in four steps"}</h2></Reveal>
          <Reveal className="mt-10" stagger>
            <ol className="grid gap-8 md:grid-cols-2 lg:grid-cols-4">
              {install.map(([I, title, desc], i) => (
                <li key={title} className="border-t-2 border-[var(--fs-leaf)] pt-5">
                  <span className="text-sm font-medium text-[var(--fs-moss)]">{i + 1}</span>
                  <I className="mt-2 h-6 w-6 text-[var(--fs-moss)]" />
                  <h3 className="mt-3 font-semibold">{title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-gray-600">{desc}</p>
                </li>
              ))}
            </ol>
          </Reveal>
        </div>
      </section>

      <section className="bg-[var(--fs-forest)] text-white">
        <div className="mx-auto flex max-w-6xl flex-col items-start gap-6 px-5 py-16 sm:px-8 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h2 className="text-3xl font-semibold tracking-[-0.02em]">{pt ? "Quer ver no seu ambiente?" : es ? "¿Quiere verlo en su entorno?" : "Want to see it in your environment?"}</h2>
            <p className="mt-2 text-white/75">{pt ? "Instalamos numa organização de teste sua e mostramos com um contrato real." : es ? "Lo instalamos en una organización de prueba suya y lo demostramos con un contrato real." : "We install in a test org of yours and demo with a real contract."}</p>
          </div>
          <Link href={WEBSITE_ROUTES.enterpriseContact}><Button size="lg" className="h-12 rounded-md bg-white px-7 text-base text-[var(--fs-forest)] hover:bg-white/90">{pt ? "Falar com vendas" : es ? "Hablar con ventas" : "Talk to sales"}</Button></Link>
        </div>
      </section>

      <footer className="py-8 text-center text-sm text-gray-500"><div className="mx-auto max-w-6xl px-5">© {new Date().getFullYear()} FillSign.</div></footer>
    </div>
  );
}
